import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  getLocations,
  getLocation,
  getLocationBySlug,
  createLocation,
  editLocation,
  deleteLocation,
  getAllLocations,
  getTrashedLocations,
  restoreLocation,
} from "api/locationApi"

export const LOCATION_QUERY_KEY = "locations"

// Get locations with pagination and filtering
export const useLocations = (params = {}, enabled = true) => {
  return useQuery({
    queryKey: [LOCATION_QUERY_KEY, params],
    queryFn: () => getLocations(params),
    keepPreviousData: true,
    enabled,
  })
}

// Get all locations (no pagination)
export const useAllLocations = (params = {}) => {
  return useQuery({
    queryKey: [LOCATION_QUERY_KEY, "all", params],
    queryFn: () => getAllLocations(params),
    keepPreviousData: true,
  })
}

// Get single location by ID
export const useLocation = (id, enabled = true) => {
  return useQuery({
    queryKey: [LOCATION_QUERY_KEY, id],
    queryFn: () => getLocation(id),
    enabled: !!id && enabled,
  })
}

// Get location by slug
export const useLocationBySlug = (slug, enabled = true) => {
  return useQuery({
    queryKey: [LOCATION_QUERY_KEY, "slug", slug],
    queryFn: () => getLocationBySlug(slug),
    enabled: !!slug && enabled,
  })
}

// Get trashed locations
export const useTrashedLocations = (params = {}) => {
  return useQuery({
    queryKey: [LOCATION_QUERY_KEY, "trashed", params],
    queryFn: () => getTrashedLocations(params),
    keepPreviousData: true,
  })
}

// Create location mutation
export const useCreateLocation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createLocation,
    onSuccess: (data) => {
      if (data?.success) queryClient.invalidateQueries([LOCATION_QUERY_KEY])
    },
  })
}

// Edit location mutation
export const useEditLocation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: editLocation,
    onSuccess: (data) => {
      if (data?.success) queryClient.invalidateQueries([LOCATION_QUERY_KEY])
    },
  })
}

// Delete location mutation
export const useDeleteLocation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteLocation,
    onSuccess: (data) => {
      if (data?.success) queryClient.invalidateQueries([LOCATION_QUERY_KEY])
    },
  })
}

// Restore location mutation
export const useRestoreLocation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreLocation,
    onSuccess: (data) => {
      if (data?.success) queryClient.invalidateQueries([LOCATION_QUERY_KEY])
    },
  })
}
